import { ModButton, ModIconButton } from '@moderneinc/design-system-components';
import {
  borderRadius,
  iconSize,
  semanticColors,
  semanticTypography,
  typography
} from '@moderneinc/design-system-tokens';
import {
  Dialog,
  Divider,
  dialogClasses,
  InputBase,
  ListSubheader,
  MenuItem,
  MenuList,
  Stack,
  Typography
} from '@mui/material';
import {
  type ChangeEvent,
  type FunctionComponent,
  type KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { SEARCH_OVERLAY_TOP_SPACING } from '../../constants/general';
import {
  CommandPaletteContext,
  type CommandPaletteItem
} from '../../contexts/command-palette.context';
import { CloseIcon, SearchIcon } from '../../icons/icons';
import { KeyboardShortcuts } from '../keyboard-shortcuts/keyboard-shortcuts.component';
import { CommandPaletteFooter } from './command-palette-footer.component';

const GROUP_ORDER = ['Actions', 'Navigation', 'Admin only'];
const LISTBOX_ID = 'command-palette-listbox';
const optionElementId = (flatIndex: number): string =>
  `command-palette-option-${flatIndex}`;

const getCommandGroup = (command: CommandPaletteItem): string => {
  if (command.group) {
    return command.group;
  }
  if (command.adminOnly) {
    return 'Admin only';
  }
  if (command.label.startsWith('Go to')) {
    return 'Navigation';
  }
  return 'Actions';
};

const getGroupOrder = (group: string): number => {
  const order = GROUP_ORDER.indexOf(group);
  return order === -1 ? GROUP_ORDER.length : order;
};

const sortCommands = (a: CommandPaletteItem, b: CommandPaletteItem): number => {
  const orderA = getGroupOrder(getCommandGroup(a));
  const orderB = getGroupOrder(getCommandGroup(b));

  if (orderA !== orderB) {
    return orderA - orderB;
  }
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }
  return 0;
};

type FlattenedCommand = {
  command: CommandPaletteItem;
  flatIndex: number;
};

type CommandGroup = {
  name: string;
  items: FlattenedCommand[];
};

export const CommandPalette: FunctionComponent = () => {
  const { commands, showPalette, togglePalette } = useContext(
    CommandPaletteContext
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const sortedCommands = useMemo(
    () => [...commands].sort(sortCommands),
    [commands]
  );

  const filteredCommands = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      return sortedCommands;
    }
    return sortedCommands.filter((cmd) =>
      cmd.label.toLowerCase().includes(query)
    );
  }, [sortedCommands, inputValue]);

  const groups = useMemo<CommandGroup[]>(() => {
    const groupMap = new Map<string, FlattenedCommand[]>();
    filteredCommands.forEach((command, flatIndex) => {
      const groupName = getCommandGroup(command);
      const flat: FlattenedCommand = { command, flatIndex };
      const existing = groupMap.get(groupName);
      if (existing) {
        existing.push(flat);
      } else {
        groupMap.set(groupName, [flat]);
      }
    });
    return Array.from(groupMap.entries()).map(([name, items]) => ({
      name,
      items
    }));
  }, [filteredCommands]);

  const activeIndex =
    filteredCommands.length === 0
      ? 0
      : Math.min(highlightedIndex, filteredCommands.length - 1);

  const hasResults = filteredCommands.length > 0;
  const activeOptionId = hasResults ? optionElementId(activeIndex) : undefined;

  useEffect(() => {
    if (!showPalette) {
      setInputValue('');
      setHighlightedIndex(0);
    }
  }, [showPalette]);

  useEffect(() => {
    if (!hasResults || !activeOptionId) {
      return;
    }
    const highlightedEl = listboxRef.current?.querySelector(
      `#${CSS.escape(activeOptionId)}`
    );
    if (highlightedEl instanceof HTMLElement) {
      highlightedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeOptionId, hasResults]);

  const runCommand = (
    command: CommandPaletteItem,
    event: KeyboardEvent | React.MouseEvent
  ): void => {
    try {
      command.action(event);
    } finally {
      togglePalette();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!hasResults) {
        return;
      }
      setHighlightedIndex(
        Math.min(activeIndex + 1, filteredCommands.length - 1)
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(Math.max(activeIndex - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const selected = filteredCommands.at(activeIndex);
      if (selected) {
        runCommand(selected, event);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      togglePalette();
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
    setHighlightedIndex(0);
  };

  const handleClearQuery = (): void => {
    setInputValue('');
    inputRef.current?.focus();
  };

  // Raw Dialog instead of ModModal: this search modal has a non-standard
  // layout (search input + scrollable results + key-hint footer) that doesn't
  // fit ModModal's header/content/footer structure. Mirrors the same pattern
  // used by global-search-input and global-organization-selector.
  return (
    <Dialog
      open={showPalette}
      onClose={togglePalette}
      aria-label="Command menu"
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: semanticColors.color.surface.scrim
          }
        }
      }}
      sx={{
        [`& .${dialogClasses.paper}`]: {
          position: 'absolute',
          top: (theme) => theme.spacing(SEARCH_OVERLAY_TOP_SPACING),
          width: '100%',
          maxWidth: 800,
          height: '80vh',
          maxHeight: 600,
          mx: 2,
          borderRadius: `${borderRadius.card}px`,
          border: `1px solid ${semanticColors.color.border.primary}`,
          overflow: 'hidden'
        }
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          sx={{
            gap: 1.5,
            alignItems: 'center',
            px: 2.5,
            py: 2
          }}
        >
          <SearchIcon
            size={iconSize.md}
            style={{
              color: semanticColors.color.icon.utility,
              flexShrink: 0
            }}
          />
          <InputBase
            inputRef={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search commands..."
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
            inputProps={{
              role: 'combobox',
              'aria-expanded': true,
              'aria-controls': LISTBOX_ID,
              'aria-autocomplete': 'list',
              'aria-activedescendant': activeOptionId
            }}
            sx={{
              fontSize: '1rem',
              '& input::placeholder': {
                color: semanticColors.color.text.inputPlaceholder,
                opacity: 1,
                WebkitTextFillColor: semanticColors.color.text.inputPlaceholder
              }
            }}
          />
          {inputValue && (
            <ModButton
              variant="tertiary"
              size="small"
              onClick={handleClearQuery}
              sx={{ flexShrink: 0 }}
            >
              Clear
            </ModButton>
          )}
          <ModIconButton
            size="small"
            onClick={togglePalette}
            aria-label="Close command menu"
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon size={'1.25rem'} />
          </ModIconButton>
        </Stack>

        <Divider />

        <MenuList
          id={LISTBOX_ID}
          ref={listboxRef}
          role="listbox"
          autoFocusItem={false}
          disablePadding
          sx={{
            flex: 1,
            overflowY: 'auto'
          }}
        >
          {groups.flatMap((group) => [
            <ListSubheader
              key={`${group.name}-header`}
              disableSticky
              disableGutters
              sx={{
                bgcolor: 'transparent',
                px: 2.5,
                pt: 2,
                pb: 1,
                lineHeight: 1.2,
                ...semanticTypography.body.small.semiBold,
                color: semanticColors.color.text.primary
              }}
            >
              {group.name}
            </ListSubheader>,
            ...group.items.map(({ command, flatIndex }) => (
              <MenuItem
                key={command.label}
                id={optionElementId(flatIndex)}
                role="option"
                aria-selected={flatIndex === activeIndex}
                selected={flatIndex === activeIndex}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onMouseEnter={() => {
                  setHighlightedIndex(flatIndex);
                }}
                onClick={(event) => {
                  runCommand(command, event);
                }}
                sx={{
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2.5,
                  py: 1.5,
                  borderBottom: `1px solid ${semanticColors.color.border.primary}`
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.fontSize.sm,
                    color: semanticColors.color.text.primary
                  }}
                >
                  {command.label}
                </Typography>
                {command.keyboardShortcuts && (
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.5
                    }}
                  >
                    <KeyboardShortcuts
                      shortcuts={command.keyboardShortcuts}
                      variant="outlined"
                    />
                  </Stack>
                )}
              </MenuItem>
            ))
          ])}
        </MenuList>

        <CommandPaletteFooter />
      </Stack>
    </Dialog>
  );
};
